const RECORDING_STATE_KEY = "analyticsRecordingState";
const RECORDINGS_DATA_KEY = "analyticsRecordingsData";

export interface AnalyticsRecording {
  request: {
    dateRange?: {
      startDate: number;
      endDate: number;
    };
    [key: string]: any;
  };
  response: any;
}

export const convertDateToDaysTillToday = (dateString: string): number => {
  const today = new Date();
  const todayUTC = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );

  const targetDate = new Date(dateString);
  const targetDateUTC = Date.UTC(
    targetDate.getUTCFullYear(),
    targetDate.getUTCMonth(),
    targetDate.getUTCDate(),
  );

  const diffInMs = todayUTC - targetDateUTC;
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

  return diffInDays;
};

export const transformDateRangeToRelative = (dateRange: {
  startDate: string;
  endDate: string;
}): { startDate: number; endDate: number } => {
  return {
    startDate: convertDateToDaysTillToday(dateRange.startDate),
    endDate: convertDateToDaysTillToday(dateRange.endDate),
  };
};

export const isRecordingActive = (): boolean => {
  try {
    const state = sessionStorage.getItem(RECORDING_STATE_KEY);
    return state === "true";
  } catch {
    return false;
  }
};

export const startRecording = (): void => {
  try {
    sessionStorage.setItem(RECORDING_STATE_KEY, "true");
    console.warn(
      "%c[Analytics Recording] Started recording.",
      "color: #4CAF50; font-weight: bold;",
    );
    console.warn(
      "%c[Analytics Recording] Use stopAnalyticsRecording() to stop.",
      "color: #2196F3;",
    );
  } catch (error) {
    console.error("[Analytics Recording] Failed to start recording:", error);
  }
};

export const stopRecording = (): void => {
  try {
    sessionStorage.setItem(RECORDING_STATE_KEY, "false");
    const recordingsCount = getRecordingsCount();
    console.warn(
      `%c[Analytics Recording] Stopped recording. ${recordingsCount} recordings captured.`,
      "color: #FF9800; font-weight: bold;",
    );
    console.warn(
      "%c[Analytics Recording] Use copyAnalyticsRecordings() to copy to clipboard.",
      "color: #2196F3;",
    );
  } catch (error) {
    console.error("[Analytics Recording] Failed to stop recording:", error);
  }
};

export const getRecordingsCount = (): number => {
  try {
    const data = sessionStorage.getItem(RECORDINGS_DATA_KEY);
    if (!data) return 0;
    const lines = data.trim().split("\n").filter(Boolean);
    return lines.length;
  } catch {
    return 0;
  }
};

const areObjectsEqual = (obj1: any, obj2: any): boolean => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

const getExistingRecordings = (): AnalyticsRecording[] => {
  const data = sessionStorage.getItem(RECORDINGS_DATA_KEY);
  if (!data) return [];

  return data
    .trim()
    .split("\n")
    .filter(Boolean)
    .map((line) => JSON.parse(line));
};

const saveRecordings = (recordings: AnalyticsRecording[]): void => {
  const ndjson = recordings.map((r) => JSON.stringify(r)).join("\n");
  sessionStorage.setItem(RECORDINGS_DATA_KEY, ndjson);
};

export const addRecording = (recording: AnalyticsRecording): void => {
  if (!isRecordingActive()) return;

  try {
    const existingRecordings = getExistingRecordings();

    const existingIndex = existingRecordings.findIndex((existing) =>
      areObjectsEqual(existing.request, recording.request),
    );

    if (existingIndex !== -1) {
      if (
        areObjectsEqual(
          existingRecordings[existingIndex].response,
          recording.response,
        )
      ) {
        return;
      } else {
        existingRecordings[existingIndex] = recording;
        saveRecordings(existingRecordings);
        return;
      }
    }

    existingRecordings.push(recording);
    saveRecordings(existingRecordings);
  } catch (error) {
    console.error("[Analytics Recording] Failed to add recording:", error);
  }
};

const legacyCopyToClipboard = (text: string): boolean => {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch {
    return false;
  }
};

export const copyRecordingsToClipboard = async (): Promise<void> => {
  const data = sessionStorage.getItem(RECORDINGS_DATA_KEY);

  if (!data) {
    console.warn(
      "%c[Analytics Recording] No recordings to copy.",
      "color: #FF5722;",
    );
    return;
  }

  const count = getRecordingsCount();

  try {
    await navigator.clipboard.writeText(data);
    console.warn(
      `%c[Analytics Recording] Copied ${count} recordings to clipboard!`,
      "color: #4CAF50; font-weight: bold;",
    );
    return;
  } catch {}

  if (legacyCopyToClipboard(data)) {
    console.warn(
      `%c[Analytics Recording] Copied ${count} recordings to clipboard!`,
      "color: #4CAF50; font-weight: bold;",
    );
    return;
  }
  console.warn(
    `%c[Analytics Recording] ${count} recordings:`,
    "color: #4CAF50; font-weight: bold;",
  );
  console.warn(data);
};

const extractApiResponse = (response: any): any => {
  if (!response) return response;

  const cleanedResponse: any = {};

  if (response.dimensionHeader !== undefined) {
    cleanedResponse.dimensionHeader = response.dimensionHeader;
  }
  if (response.metricHeaders !== undefined) {
    cleanedResponse.metricHeaders = response.metricHeaders;
  }
  if (response.rows !== undefined) {
    cleanedResponse.rows = response.rows;
  }

  return cleanedResponse;
};

export const createRecording = (
  requestPayload: any,
  response: any,
): AnalyticsRecording => {
  const recording: AnalyticsRecording = {
    request: {
      ...requestPayload,
    },
    response: extractApiResponse(response),
  };

  if (requestPayload?.dateRange) {
    recording.request.dateRange = transformDateRangeToRelative(
      requestPayload.dateRange,
    );
  }

  return recording;
};

export const registerGlobalRecordingFunctions = (): void => {
  (window as any).startAnalyticsRecording = startRecording;
  (window as any).stopAnalyticsRecording = stopRecording;
  (window as any).copyAnalyticsRecordings = copyRecordingsToClipboard;
  if (isRecordingActive()) {
    console.warn(
      "%c[Analytics Recording] Recording is active (persisted from previous session).",
      "color: #4CAF50; font-weight: bold;",
    );
  }
};
